import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styles from "./AppDownloadsBlock.module.css";
import KpiCard from "./KpiCard";
import EvolutionChart from "./EvolutionChart";
import { useDownloadStats } from "../hooks/useDownloadStats";
import { formatIntegerOrDash } from "../utils/formatNumber";
import {
    fillDailyGaps,
    getSafeFreshness,
    isDownloadRangeBeyondFreshness,
    toLocalDayKey,
} from "../utils/deriveDateBuckets";

const IOS_COLOR = "#86e3ce";
const ANDROID_COLOR = "#2bb8c8";

interface AppDownloadsBlockProps {
    /** Applied date range (drives the figures). Null = no period applied yet. */
    start: Date | null;
    end: Date | null;
}

/** Pretty short date, e.g. "28 mai 2026". Uses the active UI language so the
 *  month name matches the CMS locale, not the browser's. Falls back to raw. */
const formatDay = (iso: string | null, locale: string): string => {
    if (!iso) return "—";
    const d = new Date(`${iso.slice(0, 10)}T00:00:00.000Z`);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(locale, {
        day: "2-digit",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
    });
};

const AppDownloadsBlock: React.FC<AppDownloadsBlockProps> = ({ start, end }) => {
    const { t, i18n } = useTranslation();
    const tt = t as unknown as (k: string) => string;
    const locale = i18n.language || "fr";

    const { data, loading, forbidden, fetch, reset } = useDownloadStats();

    // Only fetch once a period has been applied — never auto-load on entry.
    // Clearing the range (reset) wipes the figures back to the empty state.
    const startMs = start ? start.getTime() : null;
    const endMs = end ? end.getTime() : null;
    const hasRange = startMs != null && endMs != null;
    useEffect(() => {
        if (hasRange) {
            fetch({ startDate: start, endDate: end });
        } else {
            reset();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startMs, endMs]);

    const series = useMemo(() => {
        if (!data || !data.series.length) return [];
        const first = new Date(data.series[0].date);
        const last = new Date(data.series[data.series.length - 1].date);
        return fillDailyGaps(first, last, data.series, () => ({ ios: 0, android: 0 }));
    }, [data]);

    // "Data not available before X" — only when the requested window starts
    // before the earliest day we actually have for that platform.
    const unavailableNotes = useMemo(() => {
        if (!data || !start) return [];
        const startKey = toLocalDayKey(start);
        const notes: string[] = [];
        const ios = data.earliestAvailable?.ios;
        const android = data.earliestAvailable?.android;
        if (ios && startKey < ios) {
            notes.push(`iOS — ${tt("appDownloadsUnavailableBefore")} ${formatDay(ios, locale)}`);
        }
        if (android && startKey < android) {
            notes.push(`Android — ${tt("appDownloadsUnavailableBefore")} ${formatDay(android, locale)}`);
        }
        return notes;
    }, [data, start, tt, locale]);

    const beyondFreshness = useMemo(() => {
        if (!hasRange || !end || !data?.dataFreshness) return false;
        return isDownloadRangeBeyondFreshness(end, data.dataFreshness);
    }, [hasRange, end, data?.dataFreshness]);

    // Server-side gating refused this viewer — render nothing.
    if (forbidden) return null;

    const safeFreshness = data?.dataFreshness ? getSafeFreshness(data.dataFreshness) : null;
    const freshnessLine =
        safeFreshness && (safeFreshness.ios || safeFreshness.android)
            ? `iOS ${tt("appDownloadsReliableThrough")} ${formatDay(safeFreshness.ios, locale)} · ` +
              `Android ${tt("appDownloadsReliableThrough")} ${formatDay(safeFreshness.android, locale)}`
            : null;

    const waitingForReports = hasRange && !loading && beyondFreshness;
    const showDownloadsContent = hasRange && Boolean(data) && !beyondFreshness;

    return (
        <section className={styles.root} aria-label={tt("appDownloadsTitle")}>
            <header className={styles.header}>
                <h3 className={styles.title}>{tt("appDownloadsTitle")}</h3>
                <p className={styles.subtitle}>{tt("appDownloadsSubtitle")}</p>
            </header>

            {loading && hasRange ? <p className={styles.empty}>{tt("loading")}</p> : null}

            {waitingForReports ? (
                <div className={styles.pending}>
                    <p className={styles.pendingMessage}>{tt("appDownloadsAwaitingReports")}</p>
                    <p className={styles.pendingHint}>{tt("appDownloadsAwaitingReportsHint")}</p>
                    {freshnessLine ? <p className={styles.freshness}>{freshnessLine}</p> : null}
                </div>
            ) : null}

            {showDownloadsContent ? (
                <>
                    <div className={styles.kpiGrid}>
                        <KpiCard
                            title={tt("appDownloadsTotal")}
                            value={formatIntegerOrDash(data?.total)}
                            help={tt("appDownloadsTotalHelp")}
                        />
                        <KpiCard
                            title={tt("appDownloadsIos")}
                            value={formatIntegerOrDash(data?.ios)}
                            help={tt("appDownloadsIosHelp")}
                        />
                        <KpiCard
                            title={tt("appDownloadsAndroid")}
                            value={formatIntegerOrDash(data?.android)}
                            help={tt("appDownloadsAndroidHelp")}
                        />
                    </div>

                    <p className={styles.disclaimer}>{tt("appDownloadsStoreDelayDisclaimer")}</p>

                    {freshnessLine ? <p className={styles.freshness}>{freshnessLine}</p> : null}

                    {unavailableNotes.map((note) => (
                        <p key={note} className={styles.warning}>
                            {note}
                        </p>
                    ))}

                    <EvolutionChart
                        title={tt("appDownloadsEvolution")}
                        data={series}
                        seriesDefs={[
                            { key: "ios", label: tt("appDownloadsIos"), color: IOS_COLOR },
                            { key: "android", label: tt("appDownloadsAndroid"), color: ANDROID_COLOR },
                        ]}
                        emptyLabel={tt("noDataInRange")}
                    />
                </>
            ) : null}

            {!hasRange ? <p className={styles.empty}>{tt("appDownloadsSelectPeriod")}</p> : null}
        </section>
    );
};

export default AppDownloadsBlock;
